import { Head, Link, usePage } from '@inertiajs/react';
import React, { useState, Suspense, lazy, useEffect, useMemo, useRef } from 'react';
import LandingHeader from './Sections/LandingHeader';
import LandingFooter from './Sections/LandingFooter';
import BLOCK_COMPONENTS from './blockRegistry';
import SectionDecorations from './Sections/SectionDecorations';
import ErrorBoundary from '../../Components/ErrorBoundary';
import { resolveDownloadLinks } from './utils';
import Lenis from 'lenis';
import { onCLS, onINP, onLCP } from 'web-vitals';
import { JsonLd } from 'react-schemaorg';
// ThumbHash available: import { thumbHashToDataURL } from 'thumbhash'

const LazySection = ({ children, skip }) => {
    const [visible, setVisible] = useState(skip);
    const ref = useRef(null);
    useEffect(() => {
        if (skip) return;
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); }
        }, { rootMargin: '200px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [skip]);
    if (!visible) return <div ref={ref} style={{ minHeight: '200px' }} />;
    return children;
};

const StaggerWrap = ({ enabled, children, className, style, id }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (!enabled) return;
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                el.classList.add('lm-stagger-visible');
                observer.unobserve(el);
            }
        }, { threshold: 0.1 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [enabled]);
    return <div ref={ref} id={id} style={style} className={`${enabled ? 'lm-stagger' : ''} ${className || ''}`.trim() || undefined}>{children}</div>;
};

const OVERLAP_MAP = { sm: '-2rem', md: '-4rem', lg: '-6rem' };
const VISIBILITY_MAP = { 'desktop-only': 'hidden lg:block', 'mobile-only': 'block lg:hidden' };

const StickyBarSection = lazy(() => import('./Sections/StickyBarSection'));
const PopupSection = lazy(() => import('./Sections/PopupSection'));
const CookieConsentSection = lazy(() => import('./Sections/CookieConsentSection'));
const NotificationBarSection = lazy(() => import('./Sections/NotificationBarSection'));
const ExitIntentSection = lazy(() => import('./Sections/ExitIntentSection'));
const FloatingCtaSection = lazy(() => import('./Sections/FloatingCtaSection'));
const SocialProofLiveSection = lazy(() => import('./Sections/SocialProofLiveSection'));

const SectionFallback = () => <div className="py-20 text-center text-gray-300 animate-pulse" aria-live="polite" aria-busy="true">Cargando...</div>;


// Blocks that auto-generate nav links (type → default label)
const NAV_LABELS = {
    about: 'Nosotros', careers: 'Carreras', services: 'Servicios', features: 'Caracteristicas',
    pricing: 'Precios', testimonials: 'Testimonios', faq: 'Preguntas frecuentes',
    team: 'Equipo', contact: 'Contacto', locations: 'Sedes', portfolio: 'Portafolio',
    blog: 'Blog', events: 'Eventos', gallery: 'Galeria', programs: 'Programas',
    process: 'Proceso', catalog: 'Catalogo', schedule: 'Horarios', menu: 'Menu',
};

// Default configs for header/footer fallback
const DEFAULT_HEADER_CONFIG = { variant: 'transparent', show_logo: true, show_nav: true, show_login: true, show_brands: true, shadow: true, blur: true, height: 'normal' };
const DEFAULT_FOOTER_CONFIG = { variant: 'full', show_logo: true, show_contact: true, show_nav: true, show_social: true, show_copyright: true };

const AdminFloatingButton = () => {
    const user = usePage().props.auth?.user;
    const [dismissed, setDismissed] = useState(false);

    if (!user || dismissed) return null;

    return (
        <div className="fixed bottom-6 left-6 z-50 flex items-center gap-2 animate-slideInLeft">
            <a href="/dashboard"
                className="flex items-center gap-2 bg-gray-900/90 hover:bg-gray-900 text-white pl-3 pr-4 py-2.5 rounded-full shadow-lg hover:shadow-xl transition-all text-sm font-medium backdrop-blur-sm">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Dashboard
            </a>
            <button onClick={() => setDismissed(true)}
                className="w-7 h-7 flex items-center justify-center bg-gray-900/70 hover:bg-gray-900 text-white/70 hover:text-white rounded-full transition-all backdrop-blur-sm"
                title="Cerrar">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
};

const PreviewBadge = () => {
    const [pos, setPos] = useState({ x: 0, y: 8 });
    const [dragging, setDragging] = useState(false);
    const offset = useRef({ x: 0, y: 0 });
    const badgeRef = useRef(null);

    useEffect(() => {
        if (!badgeRef.current) return;
        // Center horizontally on mount
        const w = badgeRef.current.offsetWidth;
        setPos({ x: (window.innerWidth - w) / 2, y: 8 });
    }, []);

    useEffect(() => {
        if (!dragging) return;
        const onMove = (e) => {
            const cx = e.touches ? e.touches[0].clientX : e.clientX;
            const cy = e.touches ? e.touches[0].clientY : e.clientY;
            setPos({ x: cx - offset.current.x, y: cy - offset.current.y });
        };
        const onUp = () => setDragging(false);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
    }, [dragging]);

    const onDown = (e) => {
        e.preventDefault();
        const cx = e.touches ? e.touches[0].clientX : e.clientX;
        const cy = e.touches ? e.touches[0].clientY : e.clientY;
        offset.current = { x: cx - pos.x, y: cy - pos.y };
        setDragging(true);
    };

    return (
        <div
            ref={badgeRef}
            onMouseDown={onDown}
            onTouchStart={onDown}
            style={{ position: 'fixed', left: pos.x, top: pos.y, zIndex: 9999, cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' }}
            className="bg-amber-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5"
        >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
            PREVIEW
        </div>
    );
};

export default function Show({ brand, blocks = [], pages = [], currentPage, mediaFolders = {}, structuredData, canonical_url, isPreview = false }) {
    const primaryColor = brand.primary_color || '#2563eb';
    const secondaryColor = brand.secondary_color || '#1e40af';

    // Scroll to top on page load/refresh
    useEffect(() => {
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        window.scrollTo(0, 0);
    }, []);

    // Google Analytics
    useEffect(() => {
        if (!brand.analytics_id) return;
        const s = document.createElement('script');
        s.async = true;
        s.id = 'ga-script';
        s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(brand.analytics_id)}`;
        document.head.appendChild(s);
        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        gtag('js', new Date());
        gtag('config', brand.analytics_id);
        return () => { document.getElementById('ga-script')?.remove(); };
    }, [brand.analytics_id]);

    // Google Tag Manager
    useEffect(() => {
        if (!brand.gtm_id) return;
        const id = brand.gtm_id;
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.id='gtm-script';j.src='https://www.googletagmanager.com/gtm.js?id='+i;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer',id);
        return () => { document.getElementById('gtm-script')?.remove(); };
    }, [brand.gtm_id]);

    // Meta Pixel (Facebook)
    useEffect(() => {
        if (!brand.meta_pixel_id) return;
        const id = brand.meta_pixel_id;
        const script = document.createElement('script');
        script.id = 'meta-pixel-script';
        script.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${id}');fbq('track','PageView');`;
        document.head.appendChild(script);
        const noscript = document.createElement('noscript');
        noscript.id = 'meta-pixel-noscript';
        noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1"/>`;
        document.head.appendChild(noscript);
        return () => { document.getElementById('meta-pixel-script')?.remove(); document.getElementById('meta-pixel-noscript')?.remove(); };
    }, [brand.meta_pixel_id]);

    // Microsoft Clarity
    useEffect(() => {
        if (!brand.clarity_id) return;
        const id = brand.clarity_id;
        const script = document.createElement('script');
        script.id = 'clarity-script';
        script.textContent = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)})(window,document,"clarity","script","${id}");`;
        document.head.appendChild(script);
        return () => { document.getElementById('clarity-script')?.remove(); };
    }, [brand.clarity_id]);

    // Sanitize custom script HTML: only allow safe tags (script, noscript, link, meta, style)
    const SAFE_SCRIPT_TAGS = new Set(['SCRIPT', 'NOSCRIPT', 'LINK', 'META', 'STYLE']);
    const injectSanitizedScripts = (html, target) => {
        const container = document.createElement('div');
        container.innerHTML = html;
        const nodes = [];
        Array.from(container.childNodes).forEach(node => {
            if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.COMMENT_NODE) return;
            if (node.nodeType !== Node.ELEMENT_NODE) return;
            if (!SAFE_SCRIPT_TAGS.has(node.nodeName)) return;
            if (node.nodeName === 'SCRIPT') {
                const script = document.createElement('script');
                Array.from(node.attributes).forEach(attr => script.setAttribute(attr.name, attr.value));
                script.textContent = node.textContent;
                target.appendChild(script);
                nodes.push(script);
            } else {
                const clone = node.cloneNode(true);
                target.appendChild(clone);
                nodes.push(clone);
            }
        });
        return nodes;
    };

    // Custom head scripts
    useEffect(() => {
        if (!brand.head_scripts) return;
        const nodes = injectSanitizedScripts(brand.head_scripts, document.head);
        return () => nodes.forEach(n => n.remove());
    }, [brand.head_scripts]);

    // Custom body scripts
    useEffect(() => {
        if (!brand.body_scripts) return;
        const nodes = injectSanitizedScripts(brand.body_scripts, document.body);
        return () => nodes.forEach(n => n.remove());
    }, [brand.body_scripts]);

    // Web Vitals — log to console in dev, silent in prod (no backend endpoint)
    useEffect(() => {
        if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
            const log = (m) => console.debug(`[Vitals] ${m.name}: ${m.value.toFixed(1)} (${m.rating})`);
            onCLS(log); onINP(log); onLCP(log);
        }
    }, []);

    const enableAnimations = brand.enable_animations !== false;
    const cardStyle = brand.card_style || 'shadow';

    // Dark mode (disabled — reserved for future configurable feature)
    const darkMode = false;

    // Lenis smooth scroll
    useEffect(() => {
        if (!enableAnimations) return;
        const lenis = new Lenis();
        let raf;
        function loop(time) { lenis.raf(time); raf = requestAnimationFrame(loop); }
        raf = requestAnimationFrame(loop);
        return () => { cancelAnimationFrame(raf); lenis.destroy(); };
    }, [enableAnimations]);

    // Extract header/footer blocks, separate content blocks
    const headerBlock = blocks.find(b => b.type === 'header') || { id: 'default-header', type: 'header', config: DEFAULT_HEADER_CONFIG, content: {} };
    const footerBlock = blocks.find(b => b.type === 'footer') || { id: 'default-footer', type: 'footer', config: DEFAULT_FOOTER_CONFIG, content: {} };
    const headerMode = headerBlock.config?.header_mode || 'overlay';
    const headerVariant = headerBlock.config?.variant || 'transparent';
    const NO_TOP_PAD = ['bottom-bar', 'scroll-reveal'];
    const isOverlay = headerMode === 'overlay' && !NO_TOP_PAD.includes(headerVariant);

    // For overlay mode: measure real header height to compensate first block
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(() => {
        // Fallback estimate until DOM measurement
        const hc = headerBlock.config || {};
        const h = hc.height || 'normal';
        let px = h === 'compact' ? 48 : h === 'large' ? 80 : 64;
        if (hc.variant === 'topbar') px += 32;
        if (hc.announcement_text) px += 36;
        return px;
    });
    useEffect(() => {
        if (!isOverlay) return;
        const wrapper = headerRef.current;
        if (!wrapper) return;
        const measure = () => {
            // Try multiple selectors to find the header element
            const el = wrapper.querySelector('[class*="fixed"]') || wrapper.querySelector('[class*="sticky"]') || wrapper.querySelector('nav') || wrapper.firstElementChild;
            if (el) { const h = el.getBoundingClientRect().height; if (h > 0) setHeaderHeight(h); }
        };
        // Measure immediately + after render
        measure();
        const timer = setTimeout(measure, 150);
        const ro = new ResizeObserver(measure);
        Array.from(wrapper.children).forEach(c => { ro.observe(c); Array.from(c.children).forEach(gc => ro.observe(gc)); });
        // Also re-measure on resize (mobile orientation changes)
        window.addEventListener('resize', measure);
        return () => { clearTimeout(timer); ro.disconnect(); window.removeEventListener('resize', measure); };
    }, [headerBlock, isOverlay]);
    const OVERLAY_TYPES = ['sticky_bar', 'popup', 'cookie_consent', 'notification_bar', 'exit_intent', 'floating_cta', 'social_proof_live'];
    // Pre-resolve download links (download:{folderId}:{fileId} -> download:{s3Url})
    const resolvedBlocks = useMemo(() => resolveDownloadLinks(blocks, mediaFolders), [blocks, mediaFolders]);
    const contentBlocks = resolvedBlocks.filter(b => b.type !== 'header' && b.type !== 'footer' && b.type !== 'error_404' && !OVERLAY_TYPES.includes(b.type));
    const stickyBarBlock = blocks.find(b => b.type === 'sticky_bar' && b.active !== false);
    const popupBlock = blocks.find(b => b.type === 'popup' && b.active !== false);
    const cookieConsentBlock = blocks.find(b => b.type === 'cookie_consent' && b.active !== false);
    const notificationBarBlock = blocks.find(b => b.type === 'notification_bar' && b.active !== false);
    const exitIntentBlock = blocks.find(b => b.type === 'exit_intent' && b.active !== false);
    const floatingCtaBlock = blocks.find(b => b.type === 'floating_cta' && b.active !== false);
    const socialProofLiveBlock = blocks.find(b => b.type === 'social_proof_live' && b.active !== false);

    // Data passed to all blocks
    const sharedProps = useMemo(() => ({ brand, mediaFolders, primaryColor, secondaryColor, enableAnimations, cardStyle, currentPage }), [brand, mediaFolders, primaryColor, secondaryColor, enableAnimations, cardStyle, currentPage]);

    // Build nav links: use manual nav_items from header content if available, otherwise auto-detect
    const headerNavItems = headerBlock?.content?.nav_items;
    const navLinks = useMemo(() => (headerNavItems && headerNavItems.length > 0)
        ? headerNavItems.map(item => {
            if (item.type === 'anchor') return {id: item.target, label: item.label, badge: item.badge};
            if (item.type === 'page') return {id: `page-${item.target}`, label: item.label, badge: item.badge, href: `/${item.target}`};
            if (item.type === 'dropdown') return {id: `dd-${item.label}`, label: item.label, badge: item.badge, dropdown: true, dropdown_style: item.dropdown_style, featured: item.featured, children: (item.children || []).map(c => ({icon: c.icon, title: c.title, description: c.description, link: c.link, open_new_tab: c.open_new_tab, badge: c.badge, image: c.image}))};
            return {id: `url-${item.label}`, label: item.label, badge: item.badge, href: item.target, external: item.open_new_tab};
        })
        : [
            ...contentBlocks.filter(b => NAV_LABELS[b.type]).map(b => ({
                id: b.type,
                label: b.content?.title || NAV_LABELS[b.type],
            })),
            ...pages.map(p => ({id: `page-${p.slug}`, label: p.title, href: p.url})),
        ], [headerNavItems, contentBlocks, pages]);

    const seenTypes = new Set();

    return (
        <>
            <Head>
                <title>{brand.meta_title || brand.name}</title>
                {brand.meta_description && <meta name="description" content={brand.meta_description} />}
                {brand.meta_keywords && <meta name="keywords" content={brand.meta_keywords} />}
                {brand.seo_indexable === false && <meta name="robots" content="noindex, nofollow" />}
                {canonical_url && <link rel="canonical" href={canonical_url} />}
                {brand.favicon_url && <link rel="icon" href={brand.favicon_url} type={brand.favicon_url.includes('.ico') ? 'image/x-icon' : 'image/png'} />}
                {brand.favicon_url && <link rel="shortcut icon" href={brand.favicon_url} />}
                {/* Preconnect to S3/CDN for faster image loading */}
                {brand.logo_url && (() => {
                    try {
                        const origin = new URL(brand.logo_url).origin;
                        return <link rel="preconnect" href={origin} crossOrigin="anonymous" />;
                    } catch { return null; }
                })()}
                {(brand.font_family || brand.heading_font) && <link rel="preconnect" href="https://fonts.googleapis.com" />}
                {(brand.font_family || brand.heading_font) && <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />}
                {(brand.font_family || brand.heading_font) && <link rel="dns-prefetch" href="https://fonts.googleapis.com" />}
                {brand.font_family && <link rel="stylesheet" href={`https://fonts.googleapis.com/css2?family=${brand.font_family.replace(/ /g, '+')}:wght@300;400;500;600;700;800;900&display=swap`} />}
                {brand.heading_font && brand.heading_font !== brand.font_family && <link rel="stylesheet" href={`https://fonts.googleapis.com/css2?family=${brand.heading_font.replace(/ /g, '+')}:wght@400;500;600;700;800;900&display=swap`} />}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={brand.meta_title || brand.name} />
                <meta property="og:site_name" content={brand.name} />
                <meta property="og:locale" content="es_ES" />
                {brand.meta_description && <meta property="og:description" content={brand.meta_description} />}
                {canonical_url && <meta property="og:url" content={canonical_url} />}
                {(brand.og_image_url || brand.logo_url) && <meta property="og:image" content={brand.og_image_url || brand.logo_url} />}
                {(brand.og_image_url || brand.logo_url) && <meta property="og:image:width" content="1200" />}
                {(brand.og_image_url || brand.logo_url) && <meta property="og:image:height" content="630" />}
                <meta name="twitter:card" content={brand.og_image_url ? 'summary_large_image' : 'summary'} />
                <meta name="twitter:title" content={brand.meta_title || brand.name} />
                {brand.meta_description && <meta name="twitter:description" content={brand.meta_description} />}
                {(brand.og_image_url || brand.logo_url) && <meta name="twitter:image" content={brand.og_image_url || brand.logo_url} />}
                {structuredData?.map((sd, i) => (
                    <script key={`ld-${i}`} type="application/ld+json">{JSON.stringify(sd)}</script>
                ))}
            </Head>

            {/* Preload hero image for LCP */}
            {(() => {
                const heroBlock = contentBlocks.find(b => b.type === 'hero');
                if (!heroBlock) return null;
                const c = heroBlock.content || {};
                const folderId = c.image_folder_id;
                const fileId = c.image_file_id;
                let heroSrc = c.image_url;
                let heroSrcSet = null;
                if (folderId && fileId && mediaFolders[folderId]) {
                    const found = mediaFolders[folderId].find(f => String(f.id) === String(fileId));
                    if (found) {
                        heroSrc = found.image_url;
                        if (found.variant_urls) {
                            const entries = [];
                            for (const [key, val] of Object.entries(found.variant_urls)) {
                                if (val) entries.push(`${val} ${key}`);
                            }
                            if (heroSrc && entries.length) entries.push(`${heroSrc} 2400w`);
                            heroSrcSet = entries.join(', ');
                        }
                    }
                }
                if (!heroSrc) return null;
                return <link rel="preload" as="image" href={heroSrc} imageSrcSet={heroSrcSet || undefined} imageSizes={heroSrcSet ? '100vw' : undefined} fetchpriority="high" />;
            })()}

            <div className={`lm-root min-h-screen bg-white${darkMode ? ' lm-dark' : ''}`} style={{ overflowX: 'clip', ...(isOverlay ? { '--lm-header-h': `${headerHeight}px` } : {}), ...(brand.font_family ? { fontFamily: `'${brand.font_family}', sans-serif` } : {}) }}>
                {isPreview && <PreviewBadge />}
                <a href="#lm-main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-white focus:text-indigo-600 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-semibold">
                    Saltar al contenido
                </a>
                {/* Header */}
                <div ref={headerRef}>
                    <LandingHeader block={headerBlock} brand={brand} navLinks={navLinks} primaryColor={primaryColor} secondaryColor={secondaryColor}/>
                </div>

                {/* Spacer for overlay header when first block is hero — hero image goes edge-to-edge, spacer prevents header from covering it */}
                {isOverlay && contentBlocks[0]?.type === 'hero' && (
                    <div style={{ height: headerHeight }} />
                )}

                {/* Render blocks */}
                <main id="lm-main-content">
                {contentBlocks.map((block, index) => {
                    const Component = BLOCK_COMPONENTS[block.type];
                    if (!Component) return null;
                    const isFirst = index === 0;
                    const cfg = block.config || {};
                    const overlap = OVERLAP_MAP[cfg.overlap];
                    const parallax = !!cfg.parallax;
                    const visibilityClass = VISIBILITY_MAP[cfg.visibility] || '';
                    const wrapperStyle = {
                        ...(overlap ? { marginTop: overlap, position: 'relative', zIndex: 10 } : {}),
                        ...(parallax ? { transform: 'translateZ(0)' } : {}),
                    };
                    const wrapperClass = [
                        isOverlay && isFirst && block.type !== 'hero' ? 'lm-first-block' : '',
                        parallax ? 'will-change-transform parallax-section' : '',
                        visibilityClass,
                    ].filter(Boolean).join(' ') || undefined;
                    const sectionId = seenTypes.has(block.type) ? `${block.type}-${block.id}` : (seenTypes.add(block.type), block.type);
                    return (
                        <div key={block.id} id={sectionId} style={{ containerType: 'inline-size' }}>
                            {cfg.responsive && (
                                <style>{`
                                    #${sectionId} [class*="grid-cols"]{grid-template-columns:repeat(${cfg.responsive.mobile_columns || 1},1fr)}
                                    @container(min-width:500px){#${sectionId} [class*="grid-cols"]{grid-template-columns:repeat(${cfg.responsive.tablet_columns || 2},1fr)}}
                                    @container(min-width:960px){#${sectionId} [class*="grid-cols"]{grid-template-columns:repeat(${cfg.responsive.desktop_columns || 3},1fr)}}
                                    ${cfg.responsive.mobile_hide_after ? `#${sectionId} [class*="grid-cols"]>*:nth-child(n+${cfg.responsive.mobile_hide_after + 1}){display:none}@container(min-width:500px){#${sectionId} [class*="grid-cols"]>*:nth-child(n+${cfg.responsive.mobile_hide_after + 1}){display:block}}` : ''}
                                `}</style>
                            )}
                            <LazySection skip={index < 2}>
                                <StaggerWrap enabled={!!cfg.stagger && enableAnimations}
                                    style={wrapperStyle}
                                    className={wrapperClass}>
                                    {cfg.decoration && <SectionDecorations type={cfg.decoration} accentColor={primaryColor} secondaryColor={secondaryColor} dark={false} />}
                                    <ErrorBoundary blockType={block.type} blockId={block.id}>
                                        <Suspense fallback={<SectionFallback />}>
                                            <Component block={block} {...sharedProps} isFirst={isFirst} headerHeight={isOverlay && isFirst ? headerHeight : 0} staggerEnabled={!!cfg.stagger} />
                                        </Suspense>
                                    </ErrorBoundary>
                                </StaggerWrap>
                            </LazySection>
                        </div>
                    );
                })}

                {/* Empty state when no blocks */}
                {contentBlocks.length === 0 && (
                    <div className="flex items-center justify-center min-h-screen">
                        <div className="text-center">
                            <h1 className="text-3xl font-bold text-gray-800 mb-2">{brand.name}</h1>
                            <p className="text-gray-500 mb-6">{brand.slogan || 'Pagina en construccion'}</p>
                        </div>
                    </div>
                )}

                </main>

                {/* Footer */}
                {contentBlocks.length > 0 && (
                    <LandingFooter block={footerBlock} brand={brand} navLinks={navLinks} primaryColor={primaryColor} secondaryColor={secondaryColor}/>
                )}

                {/* WhatsApp floating button */}
                {brand.whatsapp && (
                    <a href={`https://wa.me/${brand.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                        aria-label="Contactar por WhatsApp"
                        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group">
                        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-white text-gray-800 text-sm font-medium px-3 py-1.5 rounded-lg shadow-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            Escribenos
                        </span>
                    </a>
                )}

            </div>

            {/* Structured Data */}
            <JsonLd item={{
                "@context": "https://schema.org",
                "@type": brand.business_type || "Organization",
                "name": brand.name,
                "url": window.location.origin,
                ...(brand.logo_url && { "logo": brand.logo_url }),
                ...(brand.contact_email && { "email": brand.contact_email }),
                ...(brand.contact_phone && { "telephone": brand.contact_phone }),
                ...(brand.address && { "address": brand.address }),
                ...(brand.description && { "description": brand.description }),
            }} />
            {blocks.some(b => b.type === 'faq') && (() => {
                const faqBlock = blocks.find(b => b.type === 'faq');
                const items = faqBlock?.content?.items || [];
                if (items.length === 0) return null;
                return <JsonLd item={{
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": items.map(item => ({
                        "@type": "Question",
                        "name": item.question || item.title,
                        "acceptedAnswer": { "@type": "Answer", "text": item.answer || item.description || item.body }
                    }))
                }} />;
            })()}

            {/* Event schema */}
            {blocks.some(b => b.type === 'events') && (() => {
                const evBlock = blocks.find(b => b.type === 'events');
                const items = evBlock?.content?.items || [];
                if (items.length === 0) return null;
                return items.filter(e => e.title && e.date).map((event, i) => (
                    <JsonLd key={`event-${i}`} item={{
                        "@context": "https://schema.org",
                        "@type": "Event",
                        "name": event.title,
                        "startDate": event.date,
                        ...(event.location && { "location": { "@type": "Place", "name": event.location } }),
                        ...(event.description && { "description": event.description }),
                    }} />
                ));
            })()}

            {/* Video schema */}
            {blocks.some(b => b.type === 'video') && (() => {
                const vidBlock = blocks.find(b => b.type === 'video');
                const url = vidBlock?.content?.video_url;
                if (!url) return null;
                return <JsonLd item={{
                    "@context": "https://schema.org",
                    "@type": "VideoObject",
                    "name": vidBlock.content.title || brand.name,
                    ...(vidBlock.content.subtitle && { "description": vidBlock.content.subtitle }),
                    "contentUrl": url,
                }} />;
            })()}

            {/* BreadcrumbList for secondary pages */}
            {currentPage && (
                <JsonLd item={{
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": "Inicio", "item": window.location.origin },
                        { "@type": "ListItem", "position": 2, "name": currentPage.title || currentPage.slug },
                    ]
                }} />
            )}

            {/* Sticky Bar */}
            {stickyBarBlock && (
                <Suspense fallback={<SectionFallback />}>
                    <StickyBarSection block={stickyBarBlock} {...sharedProps} />
                </Suspense>
            )}

            {/* Popup */}
            {popupBlock && (
                <Suspense fallback={<SectionFallback />}>
                    <PopupSection block={popupBlock} {...sharedProps} />
                </Suspense>
            )}

            {/* Cookie Consent */}
            {cookieConsentBlock && (
                <Suspense fallback={null}>
                    <CookieConsentSection block={cookieConsentBlock} {...sharedProps} />
                </Suspense>
            )}

            {/* Notification Bar */}
            {notificationBarBlock && (
                <Suspense fallback={null}>
                    <NotificationBarSection block={notificationBarBlock} {...sharedProps} />
                </Suspense>
            )}

            {/* Exit Intent */}
            {exitIntentBlock && (
                <Suspense fallback={null}>
                    <ExitIntentSection block={exitIntentBlock} {...sharedProps} />
                </Suspense>
            )}

            {/* Floating CTA */}
            {floatingCtaBlock && (
                <Suspense fallback={null}>
                    <FloatingCtaSection block={floatingCtaBlock} {...sharedProps} />
                </Suspense>
            )}

            {/* Social Proof Live */}
            {socialProofLiveBlock && (
                <Suspense fallback={null}>
                    <SocialProofLiveSection block={socialProofLiveBlock} {...sharedProps} />
                </Suspense>
            )}

            <AdminFloatingButton />

            {/* Dark mode toggle — disabled, reserved for future configurable feature */}

            <style>{`
                html.lenis, html.lenis body { height: auto; }
                .lenis.lenis-smooth { scroll-behavior: auto; }
                .lenis.lenis-smooth [data-lenis-prevent] { overscroll-behavior: contain; }
                @keyframes slideInRight {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }
                @keyframes slideInLeft {
                    from { transform: translateX(-100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                .animate-slideInRight { animation: slideInRight 0.25s ease-out; }
                .animate-slideInLeft { animation: slideInLeft 0.3s ease-out; }
                .hero-btn-hover{transition:transform 0.4s cubic-bezier(0.1,0,0.3,1),box-shadow 0.3s,opacity 0.3s,background-color 0.3s}
                .hero-btn-hover:hover{transform:scale3d(1.08,1.08,1)}
                .hero-btn-hover:active{transform:scale3d(0.97,0.97,1)}
                .lm-root a[class*="shadow"]:hover,.lm-root button[class*="shadow"]:hover{transform:translateY(-1px);transition:transform .2s,box-shadow .2s}
                .parallax-section{background-attachment:fixed;background-size:cover}
                @keyframes lmBgGradShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
                .lm-first-block section{padding-top:calc(var(--lm-header-h,0px) + 4rem)!important}
                @media(min-width:768px){.lm-first-block section{padding-top:calc(var(--lm-header-h,0px) + 6rem)!important}}
                .lm-root>div+div>section,.lm-root>div+div>.lm-stagger>section{transition:background-color .3s ease}
                @keyframes lmStaggerIn{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
                .lm-stagger [class*="grid-cols"]>*,.lm-stagger [class*="grid "]>*{opacity:0;transform:translateY(24px)}
                .lm-stagger-visible [class*="grid-cols"]>*,.lm-stagger-visible [class*="grid "]>*{animation:lmStaggerIn .5s cubic-bezier(.16,1,.3,1) forwards}
                .lm-stagger-visible [class*="grid-cols"]>*:nth-child(1),.lm-stagger-visible [class*="grid "]>*:nth-child(1){animation-delay:0ms}
                .lm-stagger-visible [class*="grid-cols"]>*:nth-child(2),.lm-stagger-visible [class*="grid "]>*:nth-child(2){animation-delay:80ms}
                .lm-stagger-visible [class*="grid-cols"]>*:nth-child(3),.lm-stagger-visible [class*="grid "]>*:nth-child(3){animation-delay:160ms}
                .lm-stagger-visible [class*="grid-cols"]>*:nth-child(4),.lm-stagger-visible [class*="grid "]>*:nth-child(4){animation-delay:240ms}
                .lm-stagger-visible [class*="grid-cols"]>*:nth-child(5),.lm-stagger-visible [class*="grid "]>*:nth-child(5){animation-delay:320ms}
                .lm-stagger-visible [class*="grid-cols"]>*:nth-child(6),.lm-stagger-visible [class*="grid "]>*:nth-child(6){animation-delay:400ms}
                .lm-stagger-visible [class*="grid-cols"]>*:nth-child(7),.lm-stagger-visible [class*="grid "]>*:nth-child(7){animation-delay:480ms}
                .lm-stagger-visible [class*="grid-cols"]>*:nth-child(8),.lm-stagger-visible [class*="grid "]>*:nth-child(8){animation-delay:560ms}
                .lm-stagger-visible [class*="grid-cols"]>*:nth-child(9),.lm-stagger-visible [class*="grid "]>*:nth-child(9){animation-delay:640ms}
                .lm-stagger-visible [class*="grid-cols"]>*:nth-child(n+10),.lm-stagger-visible [class*="grid "]>*:nth-child(n+10){animation-delay:720ms}
                ${brand.heading_font ? `.lm-root h1,.lm-root h2,.lm-root h3,.lm-root h4,.lm-root h5,.lm-root h6{font-family:'${brand.heading_font}',sans-serif}` : ''}
                ${brand.global_radius === 'none' ? `.lm-root .rounded-2xl{border-radius:0!important}.lm-root .rounded-xl{border-radius:0!important}.lm-root .rounded-lg{border-radius:0!important}.lm-root .rounded-md{border-radius:0!important}.lm-root button,.lm-root [role="button"]{border-radius:0!important}.lm-root input,.lm-root select,.lm-root textarea{border-radius:0!important}` : ''}
                ${brand.global_radius === 'sm' ? `.lm-root .rounded-2xl{border-radius:0.5rem!important}.lm-root .rounded-xl{border-radius:0.375rem!important}.lm-root button,.lm-root [role="button"]{border-radius:0.375rem!important}.lm-root input,.lm-root select,.lm-root textarea{border-radius:0.375rem!important}` : ''}
                ${brand.global_radius === 'lg' ? `.lm-root .rounded-2xl{border-radius:1.5rem!important}.lm-root .rounded-xl{border-radius:1.25rem!important}.lm-root .rounded-lg{border-radius:1rem!important}` : ''}
                ${brand.global_radius === 'xl' ? `.lm-root .rounded-2xl{border-radius:2rem!important}.lm-root .rounded-xl{border-radius:1.5rem!important}.lm-root .rounded-lg{border-radius:1.25rem!important}.lm-root button:not(.rounded-full),.lm-root [role="button"]:not(.rounded-full){border-radius:9999px!important}` : ''}
                ${brand.spacing_scale === 'compact' ? `.lm-root .py-16{padding-top:clamp(1.5rem,1rem + 1.5vw,2.5rem);padding-bottom:clamp(1.5rem,1rem + 1.5vw,2.5rem)}.lm-root .py-12{padding-top:clamp(1.25rem,0.8rem + 1.2vw,2rem);padding-bottom:clamp(1.25rem,0.8rem + 1.2vw,2rem)}.lm-root .py-24{padding-top:clamp(2rem,1.5rem + 2vw,4rem);padding-bottom:clamp(2rem,1.5rem + 2vw,4rem)}@media(min-width:768px){.lm-root .md\\:py-24{padding-top:clamp(2.5rem,2rem + 2vw,4rem);padding-bottom:clamp(2.5rem,2rem + 2vw,4rem)}.lm-root .md\\:py-16{padding-top:clamp(2rem,1.5rem + 1.5vw,3rem);padding-bottom:clamp(2rem,1.5rem + 1.5vw,3rem)}.lm-root .md\\:py-32{padding-top:clamp(3rem,2.5rem + 2.5vw,5rem);padding-bottom:clamp(3rem,2.5rem + 2.5vw,5rem)}}` : ''}
                ${brand.spacing_scale === 'spacious' ? `.lm-root .py-16{padding-top:clamp(3.5rem,2.5rem + 2.5vw,5rem);padding-bottom:clamp(3.5rem,2.5rem + 2.5vw,5rem)}.lm-root .py-12{padding-top:clamp(2.5rem,2rem + 2vw,4rem);padding-bottom:clamp(2.5rem,2rem + 2vw,4rem)}.lm-root .py-24{padding-top:clamp(5rem,3.5rem + 3.5vw,7rem);padding-bottom:clamp(5rem,3.5rem + 3.5vw,7rem)}@media(min-width:768px){.lm-root .md\\:py-24{padding-top:clamp(5rem,3.5rem + 3.5vw,7rem);padding-bottom:clamp(5rem,3.5rem + 3.5vw,7rem)}.lm-root .md\\:py-16{padding-top:clamp(3.5rem,2.5rem + 2.5vw,5rem);padding-bottom:clamp(3.5rem,2.5rem + 2.5vw,5rem)}.lm-root .md\\:py-32{padding-top:clamp(6rem,4rem + 4.5vw,9rem);padding-bottom:clamp(6rem,4rem + 4.5vw,9rem)}}` : ''}
                /* Dark mode CSS reserved for future use */
                /* decoding="async" handled by ResponsiveImage component for all non-priority images */
                .lm-root h1{font-size:clamp(1.875rem,1.2rem + 2.5cqi,3.75rem)}
                .lm-root h2{font-size:clamp(1.5rem,1rem + 2cqi,3rem)}
                .lm-root h3{font-size:clamp(1.25rem,0.9rem + 1.5cqi,2.25rem)}

                /* ── Fluid typography (exclude buttons, badges, nav, footer, form elements) ── */
                .lm-root section p.text-lg,.lm-root section span.text-lg:not([class*="rounded-full"]),.lm-root section div.text-lg,.lm-root section li.text-lg{font-size:clamp(1rem,0.95rem + 0.25cqi,1.125rem)}
                .lm-root section p.text-xl,.lm-root section span.text-xl:not([class*="rounded-full"]),.lm-root section div.text-xl,.lm-root section li.text-xl{font-size:clamp(1.125rem,1rem + 0.4cqi,1.25rem)}
                .lm-root section p.text-2xl,.lm-root section span.text-2xl:not([class*="rounded-full"]),.lm-root section div.text-2xl,.lm-root section li.text-2xl{font-size:clamp(1.25rem,1.05rem + 0.7cqi,1.5rem)}
                .lm-root section p.text-3xl,.lm-root section span.text-3xl:not([class*="rounded-full"]),.lm-root section div.text-3xl,.lm-root section li.text-3xl{font-size:clamp(1.5rem,1.1rem + 1.2cqi,1.875rem)}
                .lm-root section p.text-4xl,.lm-root section span.text-4xl:not([class*="rounded-full"]),.lm-root section div.text-4xl,.lm-root section li.text-4xl{font-size:clamp(1.75rem,1.2rem + 1.6cqi,2.25rem)}
                .lm-root section p.text-5xl,.lm-root section span.text-5xl:not([class*="rounded-full"]),.lm-root section div.text-5xl,.lm-root section li.text-5xl{font-size:clamp(2rem,1.3rem + 2cqi,3rem)}
                .lm-root section p.text-6xl,.lm-root section span.text-6xl:not([class*="rounded-full"]),.lm-root section div.text-6xl,.lm-root section li.text-6xl{font-size:clamp(2.25rem,1.4rem + 2.5cqi,3.75rem)}
                .lm-root section p.text-7xl,.lm-root section span.text-7xl:not([class*="rounded-full"]),.lm-root section div.text-7xl,.lm-root section li.text-7xl{font-size:clamp(2.5rem,1.5rem + 3cqi,4.5rem)}

                /* ── Fluid vertical padding ── */
                .lm-root .py-12{padding-top:clamp(2rem,1.5rem + 1.5vw,3rem);padding-bottom:clamp(2rem,1.5rem + 1.5vw,3rem)}
                .lm-root .py-16{padding-top:clamp(2.5rem,1.8rem + 2vw,4rem);padding-bottom:clamp(2.5rem,1.8rem + 2vw,4rem)}
                .lm-root .py-24{padding-top:clamp(3rem,2rem + 3vw,6rem);padding-bottom:clamp(3rem,2rem + 3vw,6rem)}
                .lm-root .py-20{padding-top:clamp(3rem,2rem + 2.5vw,5rem);padding-bottom:clamp(3rem,2rem + 2.5vw,5rem)}

                /* ── Fluid horizontal padding ── */
                .lm-root .px-4{padding-left:clamp(1rem,0.5rem + 1.5vw,1.5rem);padding-right:clamp(1rem,0.5rem + 1.5vw,1.5rem)}
                .lm-root .px-6{padding-left:clamp(1.25rem,0.8rem + 1.8vw,2rem);padding-right:clamp(1.25rem,0.8rem + 1.8vw,2rem)}
                .lm-root .px-8{padding-left:clamp(1.5rem,1rem + 2vw,2.5rem);padding-right:clamp(1.5rem,1rem + 2vw,2.5rem)}

                /* ── Fluid gaps ── */
                .lm-root .gap-4{gap:clamp(0.75rem,0.5rem + 0.7vw,1rem)}
                .lm-root .gap-6{gap:clamp(1rem,0.7rem + 1vw,1.5rem)}
                .lm-root .gap-8{gap:clamp(1.25rem,0.8rem + 1.3vw,2rem)}
                .lm-root .gap-10{gap:clamp(1.5rem,1rem + 1.5vw,2.5rem)}
                .lm-root .gap-12{gap:clamp(1.75rem,1.1rem + 1.8vw,3rem)}
                .lm-root .gap-16{gap:clamp(2rem,1.2rem + 2.2vw,4rem)}

                /* ── Fluid margins ── */
                .lm-root .mb-6{margin-bottom:clamp(1rem,0.7rem + 1vw,1.5rem)}
                .lm-root .mb-8{margin-bottom:clamp(1.25rem,0.8rem + 1.3vw,2rem)}
                .lm-root .mb-10{margin-bottom:clamp(1.5rem,1rem + 1.5vw,2.5rem)}
                .lm-root .mb-12{margin-bottom:clamp(1.75rem,1.1rem + 1.8vw,3rem)}
                .lm-root .mb-14{margin-bottom:clamp(2rem,1.2rem + 2vw,3.5rem)}
                .lm-root .mb-4{margin-bottom:clamp(0.75rem,0.5rem + 0.7vw,1rem)}
                .lm-root .mb-16{margin-bottom:clamp(2.5rem,1.8rem + 2.2vw,4rem)}

                /* ── Container queries: grid columns ── */
                @container(min-width:500px){.lm-root .sm\\:grid-cols-2{grid-template-columns:repeat(2,minmax(0,1fr))}.lm-root .sm\\:grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))}}
                @container(min-width:700px){.lm-root .md\\:grid-cols-2{grid-template-columns:repeat(2,minmax(0,1fr))}.lm-root .md\\:grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))}.lm-root .md\\:grid-cols-4{grid-template-columns:repeat(4,minmax(0,1fr))}.lm-root .md\\:grid-cols-5{grid-template-columns:repeat(5,minmax(0,1fr))}}
                @container(min-width:960px){.lm-root .lg\\:grid-cols-2{grid-template-columns:repeat(2,minmax(0,1fr))}.lm-root .lg\\:grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))}.lm-root .lg\\:grid-cols-4{grid-template-columns:repeat(4,minmax(0,1fr))}.lm-root .lg\\:grid-cols-5{grid-template-columns:repeat(5,minmax(0,1fr))}.lm-root .lg\\:grid-cols-6{grid-template-columns:repeat(6,minmax(0,1fr))}}

                /* ── Container queries: flex direction ── */
                @container(min-width:500px){.lm-root .sm\\:flex-row{flex-direction:row}}
                @container(min-width:700px){.lm-root .md\\:flex-row{flex-direction:row}}

                /* ── Container queries: display ── */
                @container(min-width:700px){.lm-root .md\\:hidden{display:none}.lm-root .md\\:block{display:block}}
                @container(min-width:500px){.lm-root .sm\\:block{display:block}}

                /* ── Container queries: padding ── */
                @container(min-width:700px){.lm-root .md\\:py-12{padding-top:clamp(2rem,1.5rem + 1.5vw,3rem);padding-bottom:clamp(2rem,1.5rem + 1.5vw,3rem)}.lm-root .md\\:py-16{padding-top:clamp(3rem,2rem + 2vw,4rem);padding-bottom:clamp(3rem,2rem + 2vw,4rem)}.lm-root .md\\:py-20{padding-top:clamp(3.5rem,2.5rem + 2.5vw,5rem);padding-bottom:clamp(3.5rem,2.5rem + 2.5vw,5rem)}.lm-root .md\\:py-24{padding-top:clamp(4rem,3rem + 3vw,6rem);padding-bottom:clamp(4rem,3rem + 3vw,6rem)}.lm-root .md\\:py-32{padding-top:clamp(5rem,3.5rem + 4vw,8rem);padding-bottom:clamp(5rem,3.5rem + 4vw,8rem)}}

                /* ── Container queries: width ── */
                @container(min-width:700px){.lm-root .md\\:w-1\\/2{width:50%}.lm-root .md\\:w-5\\/12{width:41.666667%}}

                /* ── Container queries: space ── */
                @container(min-width:700px){.lm-root .md\\:space-y-0>:not([hidden])~:not([hidden]){--tw-space-y-reverse:0;margin-top:0;margin-bottom:0}}

                /* ── xl/2xl auto-scaling removed — use per-block responsive config instead ── */

                /* ── Fluid max-width for wide screens ── */
                .lm-root .max-w-7xl{max-width:clamp(80rem,75rem + 5vw,96rem)}
                .lm-root .max-w-6xl{max-width:clamp(72rem,68rem + 4vw,84rem)}
                .lm-root .max-w-5xl{max-width:clamp(64rem,60rem + 3.5vw,76rem)}

                /* ── Fluid heights for images/cards ── */
                .lm-root section .h-40{height:clamp(8rem,6rem + 5cqi,10rem)}
                .lm-root section .h-48{height:clamp(9rem,7rem + 6cqi,12rem)}
                .lm-root section .h-52{height:clamp(10rem,8rem + 6.5cqi,13rem)}
                .lm-root section .h-56{height:clamp(10.5rem,8.5rem + 7cqi,14rem)}
                .lm-root section .h-64{height:clamp(11rem,9rem + 7.5cqi,16rem)}
                .lm-root section .h-80{height:clamp(14rem,11rem + 9cqi,20rem)}
                .lm-root section .h-96{height:clamp(16rem,13rem + 10cqi,24rem)}

                /* ── Container queries: text sizing with cqi ── */
                @container(min-width:700px){.lm-root .md\\:text-lg{font-size:clamp(1rem,0.95rem + 0.25cqi,1.125rem)}.lm-root .md\\:text-xl{font-size:clamp(1.125rem,1rem + 0.4cqi,1.25rem)}.lm-root .md\\:text-2xl{font-size:clamp(1.25rem,1.05rem + 0.7cqi,1.5rem)}.lm-root .md\\:text-3xl{font-size:clamp(1.5rem,1.1rem + 1.2cqi,1.875rem)}.lm-root .md\\:text-4xl{font-size:clamp(1.75rem,1.2rem + 1.6cqi,2.25rem)}.lm-root .md\\:text-5xl{font-size:clamp(2rem,1.3rem + 2cqi,3rem)}.lm-root .md\\:text-6xl{font-size:clamp(2.25rem,1.4rem + 2.5cqi,3.75rem)}}
                @container(min-width:960px){.lm-root .lg\\:text-xl{font-size:clamp(1.125rem,1rem + 0.4cqi,1.25rem)}.lm-root .lg\\:text-2xl{font-size:clamp(1.25rem,1.05rem + 0.7cqi,1.5rem)}.lm-root .lg\\:text-3xl{font-size:clamp(1.5rem,1.1rem + 1.2cqi,1.875rem)}.lm-root .lg\\:text-4xl{font-size:clamp(1.75rem,1.2rem + 1.6cqi,2.25rem)}.lm-root .lg\\:text-5xl{font-size:clamp(2rem,1.3rem + 2cqi,3rem)}.lm-root .lg\\:text-6xl{font-size:clamp(2.25rem,1.4rem + 2.5cqi,3.75rem)}.lm-root .lg\\:text-7xl{font-size:clamp(2.5rem,1.5rem + 3cqi,4.5rem)}}
            `}</style>
        </>
    );
}
